import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { connectPaddleCheckout, reopenPaddleCheckout, validPaddleTransaction } from '@/lib/paddleCheckout';

/** Public default payment-link page, including links from Paddle's emails. */
export function CheckoutPage() {
  const [search] = useSearchParams();
  const transactionId = search.get('_ptxn');
  const vertical = search.get('vertical');
  const returnPath = vertical && /^[a-z0-9_-]+$/.test(vertical)
    ? `/subscribe/${encodeURIComponent(vertical)}` : '/settings';
  const [state, setState] = useState<'loading' | 'open' | 'completed' | 'closed' | 'error'>('loading');
  const [error, setError] = useState('');
  const sdkReady = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const valid = validPaddleTransaction(transactionId);

  useEffect(() => {
    if (!valid) return;
    let active = true;
    const connection = connectPaddleCheckout(transactionId, (event) => {
      if (!active) return;
      if (event.name === 'checkout.completed') setState('completed');
      else if (event.name === 'checkout.closed') setState((previous) => previous === 'completed' ? previous : 'closed');
      else if (event.name === 'checkout.loaded') setState('open');
      else if (event.name === 'checkout.error') { setError('Checkout could not complete. Please try again.'); setState('error'); }
    });
    connection.ready.then(() => { if (active) { sdkReady.current = true; setState((previous) => previous === 'loading' ? 'open' : previous); } }).catch((reason: unknown) => {
      if (active) { setError(reason instanceof Error ? reason.message : 'Checkout is unavailable.'); setState('error'); }
    });
    return () => { active = false; connection.disconnect(); };
  }, [valid, transactionId, attempt]);

  return (
    <main style={{ maxWidth: 560, margin: '60px auto', padding: 24, color: 'var(--sp-text)' }}>
      <h1>Checkout</h1>
      {!valid ? <p role="alert">This payment link is missing or invalid. Please start from your plan page.</p>
        : state === 'error' ? <p role="alert">{error}</p>
        : state === 'completed' ? <p role="status">Payment completed. Your plan and credits will update after payment confirmation.</p>
        : state === 'closed' ? <p role="status">Checkout closed. You can return to your account or reopen the payment form.</p>
        : <p role="status">{state === 'loading' ? 'Opening secure checkout…' : 'Complete your payment in the secure checkout.'}</p>}
      {valid && (state === 'error' || state === 'closed') ? (
        <button className="sp-btn" onClick={() => {
          if (sdkReady.current) {
            setState('loading');
            void reopenPaddleCheckout(transactionId).then(() => setState((previous) => previous === 'loading' ? 'open' : previous)).catch(() => {
              setError('Checkout could not open. Please reload this page.'); setState('error');
            });
          } else { setState('loading'); setAttempt((value) => value + 1); }
        }}>Try again</button>
      ) : null}
      <p><a href={state === 'completed' ? `${returnPath}?checkout=success` : returnPath}>Return to your account</a></p>
    </main>
  );
}
